<template>
  <div class="app-layout">
    <div class="app-header">
      <img :src="logoUrl" alt="logo" class="logo" />

      <span class="app-title">{{ getLoginInfo()?.companyName }}</span>

      <div class="toolbar">
        <el-badge :value="inboxStore.unreadCount" color="red" style="display: flex" :offset="[0, -5]" :show-zero="false" @click="openInboxDrawer">
          <el-icon size="18" :title="`您有${inboxStore.unreadCount}条未读消息`"><Message /></el-icon>
        </el-badge>

        <el-icon size="18" @click="showUserInfo" title="个人信息"><User /></el-icon>
        <el-icon size="18" @click="logout" title="退出登录"><SwitchButton /></el-icon>
      </div>
    </div>

    <div class="app-main">
      <div :class="foldSidebar ? 'fold' : ''" class="app-aside">
        <div class="fold-btn">
          <el-icon v-if="foldSidebar" title="展开" @click="foldSidebar = false"><Expand /></el-icon>
          <el-icon v-else title="折叠" @click="foldSidebar = true"><Fold /></el-icon>
        </div>

        <app-navigator :fold="foldSidebar" />
      </div>

      <div class="app-content">
        <div class="content-main">
          <div class="content-main__header">
            <el-breadcrumb separator="/">
              <template v-for="pathitem in breadcrumb" :key="pathitem.fullpath">
                <el-breadcrumb-item v-if="pathitem.disable">
                  <el-tag type="info">{{ pathitem.title }}</el-tag>
                </el-breadcrumb-item>

                <el-breadcrumb-item v-else :to="{ path: pathitem.fullpath }">
                  <el-tag type="primary">{{ pathitem.title }}</el-tag>
                </el-breadcrumb-item>
              </template>
            </el-breadcrumb>
          </div>

          <div class="content-main-body">
            <div class="content-body-box">
              <router-view />
            </div>
          </div>
        </div>

        <div class="content-footer">
          <el-link type="info" href="https://beian.miit.gov.cn/" :underline="false">蜀ICP备2025168507号-1</el-link>
          <el-text type="info">Copyright ©2025 成都唯思备安全科技有限公司. 版权所有.</el-text>
        </div>
      </div>
    </div>

    <el-dialog v-model="showUserInfoDialog" title="个人信息" width="fit-content" align-center center>
      <el-descriptions :column="3" border class="user-info-descriptions" label-width="120">
        <el-descriptions-item label="姓名" width="120">{{ userinfo.name }}</el-descriptions-item>

        <el-descriptions-item label="联系电话" width="150">{{ userinfo.telephone }}</el-descriptions-item>

        <el-descriptions-item label="入职日期" width="200">{{ userinfo.hiredate }}</el-descriptions-item>

        <el-descriptions-item label="性别" width="120">{{ userinfo.gender == 0 ? '男' : userinfo.gender === 1 ? '女' : '' }}</el-descriptions-item>

        <el-descriptions-item label="员工编号" width="150">{{ userinfo.staffId }}</el-descriptions-item>

        <el-descriptions-item label="所属机构" width="200">
          <div class="user-dept">
            <el-tag type="primary" v-for="(role, index) in roles" :key="index">
              <template v-for="(roleItem, index2) in role" :key="index2">
                <span v-if="index2 !== 0" class="split">/</span>
                <span :class="{ 'head-title': roleItem.head === userinfo.id }">{{ roleItem.title }}</span>
              </template>
            </el-tag>
          </div>
        </el-descriptions-item>

        <el-descriptions-item label="微信登录" width="200">
          <div v-if="userinfo.wechat?.trim()" class="wechat-login-cell">
            <span>已绑定</span>
            <el-button type="danger" icon="CircleClose" link @click="confirmUnbindWechat">解除绑定</el-button>
          </div>
          <el-button v-else type="primary" icon="Connection" link @click="openWechatBind">绑定</el-button>
        </el-descriptions-item>
      </el-descriptions>

      <template #footer>
        <el-button type="primary" @click="openChangePassword">修改密码</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="showChangePwdDialog" title="修改密码" width="420" align-center destroy-on-close @closed="resetChangePwdForm">
      <el-form ref="changePwdFormRef" :model="changePwdForm" :rules="changePwdRules" label-width="96" status-icon @keyup.enter="submitChangePassword">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="changePwdForm.oldPassword" type="password" placeholder="请输入原密码" show-password prefix-icon="Lock" />
        </el-form-item>
        <el-form-item label="新密码" prop="password">
          <el-input v-model="changePwdForm.password" type="password" placeholder="请输入新密码，不能与原密码相同" show-password prefix-icon="Lock" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="password2">
          <el-input v-model="changePwdForm.password2" type="password" placeholder="请再次输入新密码" show-password prefix-icon="Lock" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="showChangePwdDialog = false">取消</el-button>
        <el-button type="primary" @click="submitChangePassword">确定</el-button>
      </template>
    </el-dialog>

    <InboxDrawer v-model="showInboxDrawer" />

    <WechatBindDialog v-model="showWechatBindDialog" />
  </div>
</template>

<script setup>
import { computed, ref, reactive, onBeforeMount, watch } from 'vue'
import { getLoginInfo, clearLoginInfo, newRequest, newCall } from '@/util/http'
import { registerEventHandlers, socketClient } from '@/message'
import { useInboxStore } from '@/store/inbox'
import { useAuthStore } from '@/store/auth'
import { useRoute, useRouter } from 'vue-router'
import { getArchMap } from '@/util/arch'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getUrl, fileFolder } from '@/util/resource'
import { useTodoCountStore } from '@/store/todoCount'
import AppNavigator from './components/AppNavigator.vue'
import InboxDrawer from './components/InboxDrawer.vue'
import WechatBindDialog from './components/WechatBindDialog.vue'
import md5 from 'md5'

const inboxStore = useInboxStore()
const authStore = useAuthStore()
const todoCountStore = useTodoCountStore()
const router = useRouter()
const route = useRoute()

const foldSidebar = ref(false)
const showInboxDrawer = ref(false)
const showWechatBindDialog = ref(false)

const logoUrl = ref('')

const breadcrumb = computed(() => {
  const arr = []
  for (let item of route.matched) {
    if (item.meta.title) {
      arr.push({
        fullpath: item.path,
        title: item.meta.title,
        disable: item.meta?.layout === true || item.path === route.path
      })
    }
  }
  return arr
})

const showUserInfoDialog = ref(false)
const userInfoLoaded = ref(false)
const userinfo = reactive({
  id: null,
  name: '',
  telephone: '',
  hiredate: '',
  gender: null,
  staffId: '',
  wechat: ''
})
const roles = ref([])

onBeforeMount(() => {
  socketClient.connect().catch((error) => {
    console.error('[AppLayout] WebSocket 连接失败', error)
  })
  registerEventHandlers(inboxStore, todoCountStore)
  inboxStore.fetchUnreadCount()
  todoCountStore.fetchTodoCount()

  const loginInfo = getLoginInfo()
  const logo = loginInfo.logo
  if (logo) {
    logoUrl.value = getUrl(fileFolder.HOME_LOGO, logo)
  } else {
    import('@/assets/imgs/logo.png').then((res) => {
      logoUrl.value = res.default
    })
  }
})

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function queryScalar(q, key) {
  const v = q[key]
  if (v == null) return null
  return Array.isArray(v) ? v[0] : String(v)
}

function consumeWechatBindQuery() {
  const q = route.query
  const bindCode = queryScalar(q, 'wechat_bind_code')
  if (bindCode !== 'ok' && bindCode !== 'failed') return
  const nextQuery = { ...q }
  delete nextQuery.wechat_bind_code
  delete nextQuery.wechat_bind_msg
  if (bindCode === 'ok') {
    ElMessage.success('微信绑定成功')
    userInfoLoaded.value = false
  } else {
    const raw = queryScalar(q, 'wechat_bind_msg')
    const msg = raw ? safeDecodeURIComponent(raw) : '微信绑定失败'
    ElMessage.error(msg)
  }
  router.replace({ path: route.path, query: nextQuery })
}

watch(
  () => route.query.wechat_bind_code,
  () => consumeWechatBindQuery(),
  { immediate: true }
)

function openWechatBind() {
  showUserInfoDialog.value = false
  showWechatBindDialog.value = true
}

async function confirmUnbindWechat() {
  const wechat = userinfo.wechat?.trim()
  if (!wechat) return
  try {
    await ElMessageBox.confirm('解除绑定后将无法使用微信扫码登录该账号，确定解除？', '解除绑定', {
      type: 'warning',
      confirmButtonText: '解除绑定',
      cancelButtonText: '取消'
    })
  } catch {
    return
  }
  newRequest({
    url: 'user/unbind/wechat',
    method: 'POST',
    body: { wechat },
    loading: true,
    success: () => {
      ElMessage.success('操作成功')
      reloadUserInfo()
    }
  })
}

function showUserInfo() {
  showUserInfoDialog.value = true
  if (userInfoLoaded.value) return
  reloadUserInfo()
}

function reloadUserInfo() {
  newRequest({
    url: 'user/get/info',
    loading: true,
    success: (data) => {
      const archMap = getArchMap(data.archTree, false)
      roles.value = []
      for (const roleId of getLoginInfo().archIds) {
        const role = archMap[roleId]
        if (!role || role.length === 0) {
          continue
        }
        if (role.length > 1) {
          roles.value.push(role.slice(1))
        } else {
          roles.value.push(role)
        }
      }
      Object.assign(userinfo, data.user)
      userInfoLoaded.value = true
    }
  })
}

async function logout() {
  socketClient.close({ disableReconnect: true, clearQueue: true })
  await newCall({ url: 'logout', method: 'POST' })
  clearLoginInfo()
  authStore.clear()
  ElMessage.success('退出成功')
  router.push('/login')
}

function openInboxDrawer() {
  showInboxDrawer.value = true
}

const showChangePwdDialog = ref(false)
const changePwdFormRef = ref(null)
const changePwdForm = reactive({
  oldPassword: '',
  password: '',
  password2: ''
})
const changePwdRules = {
  oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
  password: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度需要在6到20个字符', trigger: 'blur' }
  ],
  password2: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== changePwdForm.password) {
          callback(new Error('两次输入的新密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

function openChangePassword() {
  showUserInfoDialog.value = false
  showChangePwdDialog.value = true
}

function resetChangePwdForm() {
  changePwdForm.oldPassword = ''
  changePwdForm.password = ''
  changePwdForm.password2 = ''
  changePwdFormRef.value?.clearValidate()
}

async function submitChangePassword() {
  const valid = await changePwdFormRef.value.validate().catch(() => false)
  if (!valid) return

  newRequest({
    url: 'user/updatePassword',
    method: 'POST',
    body: {
      oldPassword: md5(changePwdForm.oldPassword + ',' + changePwdForm.oldPassword),
      password: md5(changePwdForm.password + ',' + changePwdForm.password)
    },
    loading: true,
    success: () => {
      showChangePwdDialog.value = false
      ElMessage.success('密码修改成功')
    }
  })
}
</script>

<style lang="scss" scoped>
.app-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
}
.app-header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: var(--my-border);
  background-color: var(--my-bg-color);
  color: var(--my-text-color);
  padding: 0 20px;
  box-sizing: border-box;
}
.app-main {
  flex: 1;
  display: flex;
}
.app-aside {
  background-color: var(--my-bg-color);
  transition: width 0.5s;
  height: 100%;
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &.fold {
    width: 30px;
    transition: width 0.5s;
  }
}
.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.content-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  box-sizing: border-box;
}
.content-main__header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: var(--my-border);
  background-color: var(--my-bg-color);
  color: var(--my-text-color);
  box-sizing: border-box;
  padding: 0 20px;
}
.content-main-body {
  flex: 1;
  position: relative;
}
.content-body-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.content-footer {
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: var(--my-border);
  gap: 50px;
  box-sizing: border-box;
}
.app-title {
  margin-right: auto;
  font-family: '微软雅黑', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  font-weight: bold;
  font-size: 1.2rem;
}
.toolbar {
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 20px;

  & > * {
    color: var(--my-text-color);
    cursor: pointer;
  }
}
.fold-btn {
  color: var(--my-text-color);
  line-height: 2;
  cursor: pointer;
}
.head-title {
  color: red;
}
.logo {
  object-fit: contain;
  max-width: 300px;
  height: 50px;
}
.user-dept {
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}
.split {
  margin-left: 3px;
  margin-right: 3px;
}
.wechat-login-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
</style>
